import React, {useCallback, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useHistory, useParams} from "react-router-dom";
import {useSnackbar} from "notistack";
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, FormControlLabel, Grid,
    Radio, RadioGroup, Switch, TextField, Typography
} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {Autocomplete, Circle, GoogleMap, Marker, useJsApiLoader} from "@react-google-maps/api";
import {
    useGetEstimateRecipients, useGetSponsoredNotification
} from "../../../services/ContentManager";
import {getErrorMessage, post, put, SPONSORED_NOTIFICATIONS} from "../../../services/Client";
import CircularLoading from "../../../components/CircularLoading";
import NTMImageInput from "../../../components/NTMImageInput";
import {removeImagesFirebase} from "../../../services/FirebaseManager";
import {SPONSORED_NOTIFICATIONS_ROUTE} from "../../../constants/vendors";
import {useQueryClient} from "react-query";
import useGlobalStyles from "../../../services/useGlobalStyles";

// 20 punti = 0,20 euro alla convenzione 100 punti = 1. Va tenuto allineato a
// DEFAULT_COST_PER_READ di SponsoredNotificationService: qui serve solo alla stima.
export const COST_PER_READ = 20;
const mapContainerStyle = {width: '100%', height: 400};
const defaultCenter = {lat: 41.9028, lng: 12.4964}; // Rome

export default function SponsoredNotificationCreate({vendor}) {
    const {t} = useTranslation();
    const {id} = useParams();
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const globalClasses = useGlobalStyles();

    const isViewing = !!id;
    const {notification, status: notifStatus} = useGetSponsoredNotification(id, isViewing);

    const balance = vendor?.points != null ? parseFloat(vendor.points) : 0;

    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [targetType, setTargetType] = useState("ALL");
    const [latitude, setLatitude] = useState(defaultCenter.lat);
    const [longitude, setLongitude] = useState(defaultCenter.lng);
    const [radiusKm, setRadiusKm] = useState(5);
    const [loading, setLoading] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);

    const estimateEnabled = !isViewing && targetType && (targetType === 'ALL' || (latitude && longitude && radiusKm > 0));
    const {estimate, status: estimateStatus} = useGetEstimateRecipients(targetType, latitude, longitude, radiusKm, estimateEnabled);

    const autocompleteRef = useRef(null);
    const onAutocompleteLoad = useCallback((ac) => { autocompleteRef.current = ac; }, []);
    const onPlaceChanged = useCallback(() => {
        const place = autocompleteRef.current?.getPlace();
        if (place?.geometry?.location) {
            setLatitude(place.geometry.location.lat());
            setLongitude(place.geometry.location.lng());
        }
    }, []);

    const {isLoaded} = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
        libraries: ["places"]
    });

    if (isViewing && notifStatus === 'loading') return <CircularLoading/>;

    const estimateLoading = estimateEnabled && estimateStatus === 'loading';
    const estimatedCount = estimate?.estimatedCount || 0;
    const estimatedCost = estimatedCount * COST_PER_READ;

    const handleCreate = async () => {
        if (!title || !body) {
            enqueueSnackbar("Titolo e testo sono obbligatori", {variant: "error"});
            return;
        }

        const payload = {
            title, body, imageUrl,
            targetType,
            latitude: targetType === 'GEO_RADIUS' ? latitude : null,
            longitude: targetType === 'GEO_RADIUS' ? longitude : null,
            radiusKm: targetType === 'GEO_RADIUS' ? radiusKm : null
        };

        setLoading(true);
        try {
            await post(SPONSORED_NOTIFICATIONS, {body: payload});
            enqueueSnackbar(vendor?.notificationsAutoApproved ? t('notificationActivated') : t('notificationSentForReview'), {variant: "success"});
            history.push(SPONSORED_NOTIFICATIONS_ROUTE);
        } catch (e) {
            enqueueSnackbar(getErrorMessage(e), {variant: "error"});
        }
        setLoading(false);
    };

    const handleToggleActive = async (event) => {
        const active = event.target.checked;
        setToggleLoading(true);
        try {
            await put(SPONSORED_NOTIFICATIONS, {
                elem: `${id}/active`,
                params: {active}
            });
            await Promise.all([
                queryClient.invalidateQueries([SPONSORED_NOTIFICATIONS]),
                queryClient.invalidateQueries([SPONSORED_NOTIFICATIONS, {id}])
            ]);
            enqueueSnackbar(active ? t('notificationActivated') : t('deactivate'), {variant: "success"});
        } catch (e) {
            enqueueSnackbar(getErrorMessage(e), {variant: "error"});
        }
        setToggleLoading(false);
    };

    // --- Dettaglio (read-only) ---
    if (isViewing && notification) {
        const mapCenter = notification.latitude && notification.longitude
            ? {lat: notification.latitude, lng: notification.longitude} : defaultCenter;

        return (
            <Box>
                <Button
                    onClick={() => history.push(SPONSORED_NOTIFICATIONS_ROUTE)}
                    className={globalClasses.backButton}
                    sx={{mb: 2}}
                >
                    <ArrowBack className={globalClasses.backButtonIcon}/>
                    {t('goBack')}
                </Button>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
                    <Typography variant="h4">{notification.title}</Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Chip
                            label={notification.active ? t('active') : t('notActive')}
                            color={notification.active ? "success" : "default"}
                            variant={notification.active ? "filled" : "outlined"}
                        />
                        <FormControlLabel
                            label={notification.active ? t('deactivate') : t('activate')}
                            control={
                                <Switch
                                    checked={!!notification.active}
                                    onChange={handleToggleActive}
                                    disabled={toggleLoading}
                                />
                            }
                        />
                    </Box>
                </Box>

                <Grid container spacing={2} mb={3}>
                    <Grid item xs={6} md={3}>
                        <Card><CardContent sx={{textAlign: 'center'}}>
                            <Typography color="textSecondary">{t('actualSent')}</Typography>
                            <Typography variant="h4">{notification.actualSent || 0}</Typography>
                        </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card><CardContent sx={{textAlign: 'center'}}>
                            <Typography color="textSecondary">{t('totalReads')}</Typography>
                            <Typography variant="h4">{notification.totalReads || 0}</Typography>
                        </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card><CardContent sx={{textAlign: 'center'}}>
                            <Typography color="textSecondary">{t('readPercentage')}</Typography>
                            <Typography variant="h4">
                                {notification.actualSent > 0
                                    ? ((notification.totalReads / notification.actualSent) * 100).toFixed(1) + '%'
                                    : '0%'}
                            </Typography>
                        </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Card><CardContent sx={{textAlign: 'center'}}>
                            <Typography color="textSecondary">{t('totalCost')}</Typography>
                            <Typography variant="h4">
                                {`${notification.totalCost ? parseFloat(notification.totalCost) : 0} ${t('points')}`}
                            </Typography>
                        </CardContent></Card>
                    </Grid>
                </Grid>

                <Card sx={{mb: 2}}>
                    <CardContent>
                        <Typography variant="body1" sx={{mb: 1}}><strong>{t('notificationBody')}:</strong> {notification.body}</Typography>
                        <Typography variant="body1" sx={{mb: 1}}><strong>{t('targetType')}:</strong> {notification.targetType === 'GEO_RADIUS' ? t('geoRadius') : t('allUsers')}</Typography>
                        {notification.targetType === 'GEO_RADIUS' && (
                            <Typography variant="body1"><strong>{t('radiusKm')}:</strong> {notification.radiusKm} km</Typography>
                        )}
                    </CardContent>
                </Card>

                {notification.targetType === 'GEO_RADIUS' && isLoaded && (
                    <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={11}>
                        <Marker position={mapCenter}/>
                        <Circle center={mapCenter} radius={(notification.radiusKm || 5) * 1000}
                            options={{strokeColor: '#1976d2', strokeWeight: 2, fillColor: '#1976d2', fillOpacity: 0.15}}/>
                    </GoogleMap>
                )}
            </Box>
        );
    }

    // --- Creazione ---
    const mapCenter = {lat: latitude, lng: longitude};

    return (
        <Box>
            <Button
                onClick={() => history.push(SPONSORED_NOTIFICATIONS_ROUTE)}
                className={globalClasses.backButton}
                sx={{mb: 2}}
            >
                <ArrowBack className={globalClasses.backButtonIcon}/>
                {t('goBack')}
            </Button>
            <Typography variant="h4" gutterBottom>{t('newSponsoredNotification')}</Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Contenuto</Typography>
                            <TextField
                                label={t('notificationTitleLabel')}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                fullWidth required sx={{mb: 2}}
                            />
                            <TextField
                                label={t('notificationBodyLabel')}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                fullWidth multiline rows={4} required sx={{mb: 2}}
                            />
                            <NTMImageInput
                                title={t('image')}
                                images={imageUrl}
                                isSingleImage={true}
                                prefix={vendor?.id || 'sponsored'}
                                folder={'sponsored-notifications/images'}
                                requirementsLabel={t('imageRequirementsNotification')}
                                onAddImage={(url) => setImageUrl(url)}
                                onDeleteImage={(img) => {
                                    removeImagesFirebase([img]);
                                    setImageUrl("");
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('notificationRecipientsQuestion')}</Typography>
                            <RadioGroup value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                                <FormControlLabel value="ALL" control={<Radio/>} label={t('allUsers')}/>
                                <FormControlLabel value="GEO_RADIUS" control={<Radio/>} label={t('geoRadiusNearShop')}/>
                            </RadioGroup>

                            {targetType === 'GEO_RADIUS' && (
                                <>
                                    <TextField
                                        label={t('radiusKm')} type="number" value={radiusKm}
                                        onChange={(e) => setRadiusKm(parseFloat(e.target.value) || 0)}
                                        fullWidth inputProps={{min: 1, max: 100, step: 1}}
                                        sx={{mb: 2, mt: 2}}
                                    />
                                    {isLoaded && (
                                        <>
                                            <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                                                <TextField
                                                    label={t('searchAddress')}
                                                    fullWidth
                                                    sx={{mb: 2}}
                                                />
                                            </Autocomplete>
                                            <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={11}>
                                                <Marker position={mapCenter} draggable
                                                    onDragEnd={(e) => { setLatitude(e.latLng.lat()); setLongitude(e.latLng.lng()); }}/>
                                                <Circle center={mapCenter} radius={radiusKm * 1000}
                                                    options={{strokeColor: '#1976d2', strokeWeight: 2, fillColor: '#1976d2', fillOpacity: 0.15}}/>
                                            </GoogleMap>
                                        </>
                                    )}
                                </>
                            )}

                            <Box mt={3} p={2} sx={{backgroundColor: '#f5f5f5', borderRadius: 2}}>
                                <Typography variant="body1">
                                    <strong>{t('estimatedRecipients')}:</strong>{' '}
                                    {estimateLoading
                                        ? <CircularProgress size={14} sx={{ml: 1, verticalAlign: 'middle'}}/>
                                        : estimatedCount}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t('costPerRead')}:</strong> {COST_PER_READ} {t('points')}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t('estimatedCost')}:</strong>{' '}
                                    {estimateLoading
                                        ? <CircularProgress size={14} sx={{ml: 1, verticalAlign: 'middle'}}/>
                                        : `${estimatedCost} ${t('points')} (${t('estimatedCostHint')})`}
                                </Typography>
                                <Chip
                                    label={`${t('balance')}: ${balance} ${t('points')}`}
                                    color={balance >= estimatedCost ? "success" : "error"}
                                    sx={{mt: 1}}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box mt={3}>
                {/* Dice prima del click se la notifica parte o si mette in coda. */}
                <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                    {vendor?.notificationsAutoApproved
                        ? t('notificationSendsWithoutReview')
                        : t('notificationWillBeReviewed')}
                </Typography>
                <Button
                    variant="contained" color="primary"
                    onClick={handleCreate} disabled={loading}
                >
                    {vendor?.notificationsAutoApproved ? t('activateNotification') : t('notificationSendToReview')}
                </Button>
            </Box>
        </Box>
    );
}
